import { Page } from "../Page.js";
import { h, patch } from "../dom.js";
import { ago, compact, time } from "../format.js";
import { butlerVersionStatus, describeButlerVersion } from "../../shared/butlerVersion.js";

/** Link status and AE power. */
export class NetworkPage extends Page {
  readonly title = "Network";
  private readonly link = h("div", { class: "card" });
  private readonly power = h("div", { class: "card" });
  private readonly counts = h("div", { class: "card" });

  mount(): void {
    this.el.append(h("div", { class: "cards" }, this.link, this.power, this.counts));
    this.listen("link", () => this.render());
    this.listen("power", () => this.render());
    this.listen("cpus", () => this.render());
    this.listen("craftables", () => this.render());
    this.listen("socket", () => this.render());
    this.listen("rules", () => this.render());
    this.every(10_000, () => this.render());
    this.render();
  }

  private versionInfo(v: string | null): HTMLElement {
    const s = butlerVersionStatus(v);
    const badge =
      s.state === "outdated" ? h("span", { class: "badge low" }, "Outdated")
      : s.state === "current" ? h("span", { class: "badge ok" }, "Up to date")
      : h("span", { class: "badge backoff" }, s.state === "newer" ? "Newer than app" : "Unknown");
    return h(
      "div",
      { class: "version" },
      h("p", null, badge, ` ${describeButlerVersion(s)}`),
      s.state === "outdated"
        ? h(
            "div",
            { class: "small" },
            h("p", null, "Missing:"),
            h("ul", null, ...s.missing.map((m) => h("li", null, m))),
            h("p", { class: "muted" }, "On the computer, run /ME-OC-Butler/update.lua from a fresh copy of the repo (or butler-update once installed). It ends with \"Updated OC Butler <old> -> <new>\" and the Butler reconnects with the new version."),
          )
        : null,
    );
  }

  private render(): void {
    const live = this.ctx.live;
    const link = live.link;
    patch(this.link, 
      h("h2", null, "Game link"),
      link?.connected
        ? h("div", null, h("p", null, h("span", { class: "badge ok" }, "Connected"), ` Butler "${link.butler}"`), this.versionInfo(link.version), h("p", { class: "muted small" }, `Since ${link.since ? time(link.since) : "?"} from ${link.remote ?? "?"}`))
        : h("div", null, h("p", null, h("span", { class: "badge low" }, "Disconnected")), h("p", { class: "muted small" }, link?.since ? `Last connected ${time(link.since)}. The computer stocks its critical rules while the app is unreachable.` : "The Butler has not connected since the app started.")),
      live.socketOpen
        ? h("p", { class: "muted small" }, "Dashboard live updates: on")
        : live.polling
          ? h("p", { class: "small warn" }, "Dashboard live updates: the WebSocket can't connect, so this page refreshes every 5 s. Behind nginx, add the Upgrade and Connection headers shown in deploy/nginx.conf.")
          : h("p", { class: "muted small" }, "Dashboard live updates: connecting…"),
    );
    const p = live.power;
    const pct = p && p.max ? Math.min(100, (p.stored / p.max) * 100) : 0;
    patch(this.power, 
      h("h2", null, "AE power"),
      p
        ? h(
            "div",
            null,
            h("div", { class: "bar big" }, h("div", { class: `fill ${pct < 10 ? "low" : ""}`, style: `width:${pct}%` })),
            h("p", null, `${compact(p.stored)} / ${compact(p.max)} AE stored (${pct.toFixed(1)}%)`),
            h("dl", { class: "stats" }, h("dt", null, "Usage"), h("dd", null, `${compact(p.usage)} AE/t`), h("dt", null, "Injection"), h("dd", null, `${compact(p.injection)} AE/t`), h("dt", null, "Idle"), h("dd", null, `${compact(p.idle)} AE/t`)),
            h("p", { class: "muted small" }, `Updated ${ago(p.at)}`),
          )
        : h("p", { class: "muted" }, "No power report yet."),
    );
    const snap = live.snapshot;
    patch(this.counts, 
      h("h2", null, "Butler"),
      h(
        "dl",
        { class: "stats" },
        h("dt", null, "Rules"),
        h("dd", null, String(snap?.ruleCount ?? 0)),
        h("dt", null, "Crafting CPUs"),
        h("dd", null, `${live.cpus.filter((c) => c.busy).length} busy of ${live.cpus.length}`),
        h("dt", null, "Craftables cached"),
        h("dd", null, `${snap?.craftablesCount ?? 0} (${ago(snap?.craftablesAt ?? null)})`),
      ),
    );
  }
}
